import * as Ariakit from '@ariakit/react';
import cn from 'classnames';
import { SelectButton } from '#components/atoms/buttons/select-button/SelectButton.tsx';
import { HelpTip } from '#components/atoms/forms/help-tip/HelpTip.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectMenu } from '../select-menu/SelectMenu.tsx';
import { SelectProvider } from '../SelectContext.tsx';

const DEFAULT_PLACEHOLDER = 'Please select…';

type Props<T extends string> = {
  /** Text to use as the label */
  label: React.ReactNode;
  /** Contents to put into a HelpTip after the label, on the same line */
  helpTip?: React.ReactNode;
  /** When you want a label, but don't want it to show up on screen */
  visuallyHideLabel?: boolean;
  defaultValue?: T;
  /** A HelpText component or similar, to show between label and toggle button */
  help?: React.ReactNode;
  /** String to show if no value is selected */
  placeholder?: string;
  /** Adds a red border around the toggle button */
  hasError?: boolean;
  /** Adds a red border around the toggle button and shows the message if defined */
  errorMessage?: string;
  /** A ref to pass to the toggle button */
  innerRef?: React.Ref<HTMLButtonElement>;
  /** When disabled, the toggle button will not be active */
  disabled?: boolean;
  /** Blur handler for the toggle button */
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  renderValue?: (value: T) => string | null;
  /** Additional classes to apply to container element */
  className?: string;
  /** Additional elements to place in the {@link Field} that is created (for a11y) */
  fieldChildren?: React.ReactNode;
  children: React.ReactNode;
  /** Controls the value of the Select. Leave undefined to keep the Select uncontrolled. */
  value?: T;
  /** Props to pass to Ariakit's useSelectStore */
  storeProps?: Ariakit.SelectStoreProps;
  /**
   * an onChange e.g. from react-hook-form.
   * Only called when the selected value actually changes — not on mount, and not as an echo
   * of an external `value` prop update.
   */
  onChange?: (value: T) => void;
};

export function Select<T extends string>({ innerRef, ...props }: Props<T>) {
  const { placeholder = DEFAULT_PLACEHOLDER, onChange } = props;
  const select = Ariakit.useSelectStore({
    ...props.storeProps,
    defaultValue: props.defaultValue,
    // Ariakit's native controlled mode: `undefined` keeps the store uncontrolled. Handing the
    // prop to the store (rather than syncing it in with an effect) lets ariakit reconcile
    // controlled updates internally — the effect-based sync could crash with "Maximum update
    // depth exceeded" when a controlled Select mounted with a non-empty value while its
    // SelectItems were still registering.
    value: props.value,
    // Fires for value changes from clicks and key presses alike, but only when the value
    // actually changes — not on mount, and not as an echo of a `value` prop update.
    setValue: (newValue) => onChange?.(newValue as T),
  });
  const isOpen = Ariakit.useStoreState(select, 'open');
  const value = Ariakit.useStoreState(select, 'value') as T; // Can't figure out any better way right now

  return (
    <SelectProvider store={select}>
      <Field
        className={props.className}
        label={
          <div className={cn('label flex align-center', { 'visually-hidden': props.visuallyHideLabel })}>
            <Ariakit.SelectLabel store={select}>{props.label}</Ariakit.SelectLabel>
            {props.helpTip ?
              <HelpTip>{props.helpTip}</HelpTip>
            : null}
          </div>
        }
      >
        {props.help}
        {/* This extra div wrapper prevents unwanted margin on the select */}
        <div>
          <Ariakit.Select
            store={select}
            onBlur={props.onBlur}
            disabled={props.disabled}
            ref={innerRef}
            render={
              <SelectButton
                hasValue={Boolean(value)}
                hasError={props.hasError ?? Boolean(props.errorMessage)}
                isMenuOpen={isOpen}
              />
            }
          >
            {props.renderValue?.(value) ?? (value || placeholder)}
          </Ariakit.Select>
          <SelectMenu type="select" store={select}>
            {props.children}
          </SelectMenu>
        </div>

        {props.errorMessage && <ErrorMessage>{props.errorMessage}</ErrorMessage>}
        {props.fieldChildren}
      </Field>
    </SelectProvider>
  );
}
